import React, { useMemo, useState } from 'react'
import { Avatar, Col, DatePicker, Form, Input, Row, Select } from 'antd'
import CustomModal from 'components/global/CustomModal'
import CustomModalFooter from 'components/global/CustomModalFooter'
import useFormSubmit from 'helper/Hooks/formSubmit'
import FileUploader from 'helper/FileUploader'
import userAvatarSvg from 'assets/Images/userAvatar.svg'
import GradeInput from 'pages/Family/Profiles/FamilyProfile/FamilyMembers/Modals/Kid/ModalContent/GradeInput'
import { leagueAttendanceStore } from 'state/league/leagueAttendance'
import { leagueEventStore } from 'state/league/leagueEvent'
import { myStore } from 'state/store'
import {
  useCreateLeagueDropInAttendee,
} from 'api/league/LeagueAttendance/leagueAttendance'
import { useGetLeagueGames } from 'api/league/leagueGames/leagueGames'
import { LeagueGamesByRoundDate } from 'types/league/leagueGame'
import { validateAlphabets } from 'helper/utility'
import DivisionSelect from 'pages/Events/League/Modals/ModalContent/Inputs/DivisionSelect'
import moment from 'moment'

const AddDropInModal: React.FC = () => {
  const { form, handleSubmit } = useFormSubmit()
  const [avatar, setAvatar] = useState<any>()
  const [attendeeType, setAttendeeType] = useState<'kid' | 'guardian' | 'authorized-adult'>('kid')

  const {
    addDropInModalOpen,
    setAddDropInModalOpen,
  } = leagueAttendanceStore(store => ({
    addDropInModalOpen: store.addDropInModalOpen,
    setAddDropInModalOpen: store.setAddDropInModalOpen,
  }))

  const { leagueData } = leagueEventStore(store => ({ leagueData: store.leagueData }))
  const setModalBtnLoader = myStore(store => store.setModalBtnLoader)

  const { mutate: createDropIn } = useCreateLeagueDropInAttendee()

  const { data: gamesData, refetch: refetchGames } = useGetLeagueGames({
    leagueId: leagueData?._id || '',
  })

  React.useEffect(() => {
    if (addDropInModalOpen && leagueData?._id) {
      refetchGames()
    }
  }, [addDropInModalOpen])

  const flatGames = useMemo(() => {
    if (!gamesData) return []
    return gamesData.flatMap((roundGroup: LeagueGamesByRoundDate) =>
      roundGroup.dates.flatMap(dateGroup =>
        dateGroup.games.map(game => ({
          ...game,
          roundLabel: `R${roundGroup.round} — ${new Date(game.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — ${game.homeTeam?.team?.name || 'Home'} vs ${game.awayTeam?.team?.name || 'Away'}`,
        }))
      )
    )
  }, [gamesData])

  const closeModal = () => {
    form.resetFields()
    setAvatar(undefined)
    setAttendeeType('kid')
    setAddDropInModalOpen(false)
  }

  const onFinish = (values: any) => {
    const payload = {
      ...values,
      leagueId: leagueData._id,
      dob: values.dob ? moment(values.dob).format('YYYY-MM-DD') : undefined,
      type: attendeeType,
    }
    setModalBtnLoader(true)
    createDropIn(payload, {
      onSuccess: () => {
        setModalBtnLoader(false)
        closeModal()
      },
      onError: () => {
        setModalBtnLoader(false)
      },
    })
  }

  return (
    <CustomModal
      openModal={addDropInModalOpen}
      isCloseModalCustom
      closeModal={closeModal}
      title="Add drop-in attendee"
      width={600}
      footer={
        <CustomModalFooter
          multibutton={false}
          btnAlignment="justify-end"
          btnText="Check-in"
          btnType="primary"
          onSubmit={handleSubmit}
        />
      }
    >
      <Form form={form} onFinish={onFinish} layout="vertical">
        {/* Photo upload */}
        <Row gutter={24} align="middle" className="mb-10px">
          <Col span={4}>
            <Avatar size={80} src={avatar || userAvatarSvg} />
          </Col>
          <Col span={18}>
            <Form.Item name="photoId">
              <FileUploader
                setAvatar={setAvatar}
                name="drop-in"
                form={form}
                text="Upload a photo"
                fileId={undefined}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Name */}
        <Row gutter={24}>
          <Col span={12}>
            <label className="input-label required-input">First Name</label>
            <Form.Item
              name="firstName"
              rules={[
                { required: true, message: 'First Name required' },
                { validator: validateAlphabets },
              ]}
            >
              <Input placeholder="Enter First Name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <label className="input-label required-input">Last Name</label>
            <Form.Item
              name="lastName"
              rules={[
                { required: true, message: 'Last Name required' },
                { validator: validateAlphabets },
              ]}
            >
              <Input placeholder="Enter Last Name" />
            </Form.Item>
          </Col>
        </Row>

        {/* Gender */}
        <Row>
          <Col span={24}>
            <label className="input-label required-input">Gender</label>
            <Form.Item
              name="gender"
              rules={[{ required: true, message: 'Gender is required' }]}
            >
              <Select
                className="h-50px select-field"
                placeholder="Select gender"
                onChange={(val: string) => form.setFieldsValue({ gender: val })}
              >
                <Select.Option value="male">Male</Select.Option>
                <Select.Option value="female">Female</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Date of Birth */}
        <Row>
          <Col span={24}>
            <label className="input-label">Date of Birth</label>
            <Form.Item name="dob">
              <DatePicker className="selector w-full" />
            </Form.Item>
          </Col>
        </Row>

        {/* Grade (kid only) */}
        {attendeeType === 'kid' && (
          <Row>
            <Col span={24}>
              <label className="input-label">Grade</label>
              <Form.Item name="gradeId">
                <GradeInput form={form} gradeId="" data={undefined} />
              </Form.Item>
            </Col>
          </Row>
        )}

        {/* Attendee type */}
        <Row>
          <Col span={24}>
            <label className="input-label required-input">Attendee Type</label>
            <Form.Item name="type">
              <Select
                className="h-50px select-field"
                placeholder="Select type"
                defaultValue="kid"
                onChange={(val: 'kid' | 'guardian' | 'authorized-adult') => {
                  setAttendeeType(val)
                  form.setFieldsValue({ type: val })
                }}
              >
                <Select.Option value="kid">Kid</Select.Option>
                <Select.Option value="guardian">Guardian</Select.Option>
                <Select.Option value="authorized-adult">Authorized Adult</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Division */}
        <Row>
          <Col span={24}>
            <label className="input-label required-input">Division</label>
            <Form.Item
              name="divisionId"
              rules={[{ required: true, message: 'Division is required' }]}
            >
              <DivisionSelect form={form} data={undefined} leagueId={leagueData?._id || ''} />
            </Form.Item>
          </Col>
        </Row>

        {/* Game */}
        <Row>
          <Col span={24}>
            <label className="input-label required-input">Game</label>
            <Form.Item
              name="gameId"
              rules={[{ required: true, message: 'Game is required' }]}
            >
              <Select
                className="h-50px select-field"
                placeholder="Select game"
                onChange={(val: string) => form.setFieldsValue({ gameId: val })}
              >
                {flatGames.map((game: any) => (
                  <Select.Option key={game._id} value={game._id}>
                    {game.roundLabel}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Team type */}
        <Row>
          <Col span={24}>
            <label className="input-label required-input">Team Type</label>
            <Form.Item
              name="teamType"
              rules={[{ required: true, message: 'Team type is required' }]}
            >
              <Select
                className="h-50px select-field"
                placeholder="Select team type"
                onChange={(val: string) => form.setFieldsValue({ teamType: val })}
              >
                <Select.Option value="home">Home</Select.Option>
                <Select.Option value="away">Away</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </CustomModal>
  )
}

export default AddDropInModal
